import useIsMobile from '@/hooks/use-is-mobile';
import { formatRiskRoundTwoDecimals } from '@/utils';
import { Box, useBreakpointValue, useTheme } from '@cmpsr/components';
import { Label, Legend, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

type OverallRiskTrendChartProps = {
  risks: number[];
  riskChanges: number[];
};

export default function OverallRiskTrendChart({ risks, riskChanges }: OverallRiskTrendChartProps) {
  const { colors, textStyles } = useTheme();
  const isMobile = useIsMobile();

  const chartHeightInput = useBreakpointValue({ base: '220px', md: '250px', lg: '300px', xl: '350px' }, { ssr: false });
  const chartHeight = chartHeightInput ?? '220px';

  const chartData = risks.map((value, index) => ({
    x: index + 1,
    y: value,
  }));

  // Y-Axis Domain calculation for increased separation
  let yDomain: [number, number];
  const yPadding = 1; // Increased padding for more separation
  if (risks.length === 0) {
    yDomain = [0, 10 + yPadding * 2]; // Default domain for empty chart with padding
  } else {
    const yMin = Math.min(...risks);
    const yMax = Math.max(...risks);
    if (yMin === yMax) {
      yDomain = [yMin - yPadding, yMax + yPadding];
    } else {
      yDomain = [yMin - yPadding, yMax + yPadding];
    }
    // Ensure the domain always has min < max and some span if padding is zero or negative (though yPadding is positive here)
    if (yDomain[0] >= yDomain[1]) {
      yDomain[1] = yDomain[0] + yPadding * 2; // Ensure a minimum span
    }
  }

  // X-Axis Domain (dataKey="x", which is 1-based index)
  let xDomain: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax'];
  if (chartData.length === 0) {
    xDomain = [0.5, 1.5]; // Default for empty data, consistent with 1-based view
  } else if (chartData.length === 1) {
    xDomain = [chartData[0].x - 0.5, chartData[0].x + 0.5]; // Center the single point
  } else {
    // chartData.length > 1
    xDomain = ['dataMin', 'dataMax'];
  }

  return (
    <Box
      bgColor="background-light"
      padding={{ base: 6, md: 8, lg: 12 }}
      border="1px solid"
      borderColor="ui-element-divider"
      borderRadius="radii-4"
      mt={{ base: 5, md: 8 }}
      height={chartHeight}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            right: 40, //space for last annotation
            left: 10, // To allow legend's negative marginLeft to position it correctly
          }}
        >
          <XAxis dataKey="x" domain={xDomain} axisLine={{ stroke: colors['ui-element-divider'] }} tick={false} />
          <YAxis domain={yDomain} axisLine={{ stroke: colors['ui-element-divider'] }} tick={false} width={1} />
          <Tooltip active={false} />
          <Legend align="left" iconType="circle" />
          <Line
            type="linear"
            dataKey="y"
            name="Your overall lifetime risk trend"
            stroke={colors['primary-default']}
            strokeWidth={1.5}
            dot={{ r: 5, strokeWidth: 0, fill: colors['primary-default'] }}
            activeDot={false}
            isAnimationActive={false}
          />
          {riskChanges
            .map((value, index) =>
              _drawAnnotation({
                value,
                index,
                riskYValue: risks[index],
                colors,
                textStyles,
                isMobile,
              })
            )
            .filter(Boolean)}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

type DrawAnnotationProps = {
  value: number;
  index: number;
  riskYValue: number | undefined;
  colors: Record<string, string>;
  textStyles: Record<string, string>;
  isMobile: boolean;
};

function _drawAnnotation({
  value,
  index,
  riskYValue,
  colors,
  textStyles,
  isMobile,
}: DrawAnnotationProps): JSX.Element | null {
  // First annotation is the initial risk value, so skip it
  if (index === 0) return null;

  if (riskYValue === undefined || riskYValue === null) return null;

  const labelValue = value;
  const labelText =
    value >= 0 ? `↑ ${formatRiskRoundTwoDecimals(labelValue)}` : `↓ ${formatRiskRoundTwoDecimals(labelValue)}`;
  const pointBackgroundColor = value >= 0 ? colors['alert-error-default'] : colors['alert-success-default'];
  const labelTextColor = value >= 0 ? colors['text-error'] : colors['text-success'];
  const labelBackgroundColor = pointBackgroundColor;

  return (
    <ReferenceDot
      key={`anno-${index}`}
      x={index + 1}
      y={riskYValue}
      r={5}
      fill={pointBackgroundColor}
      stroke="transparent"
      isFront
    >
      {!isMobile && (
        <Label
          content={
            <CustomAnnotationLabel text={labelText} textColor={labelTextColor} backgroundColor={labelBackgroundColor} />
          }
        />
      )}
    </ReferenceDot>
  );
}

const CustomAnnotationLabel = (props: {
  viewBox?: { x?: number; y?: number };
  text: string;
  textColor: string;
  backgroundColor: string;
}) => {
  const { viewBox, text, textColor, backgroundColor } = props;
  const cx = viewBox?.x ?? 0;
  const cy = viewBox?.y ?? 0;

  const textStyle = {
    fontSize: '12px',
    fontWeight: 700,
  };

  const padding = { horizontal: 4, vertical: 2 };
  const textHeight = 12; // font size
  const rectHeight = textHeight + padding.vertical * 2; // 12 + 4 = 16

  const estimatedCharWidth = 7; // Average pixels per char for a 12px bold font.
  let textContentWidth = text.length * estimatedCharWidth;
  if (text.startsWith('↑') || text.startsWith('↓')) {
    textContentWidth += 5; // Adjust for wider arrow characters
  }
  const rectWidth = textContentWidth + padding.horizontal * 2;

  const groupYOffset = cy + 38; // Position label top 38px below dot center
  const groupXOffset = cx - rectWidth / 2; // Center the label horizontally

  return (
    <g transform={`translate(${groupXOffset}, ${groupYOffset})`}>
      <rect
        width={rectWidth}
        height={rectHeight}
        fill={backgroundColor}
        rx="3" // Rounded corners
        ry="3"
      />
      <text
        x={rectWidth / 2}
        y={rectHeight / 2}
        dy="0.35em" // SVG text vertical alignment
        fill={textColor}
        textAnchor="middle"
        style={textStyle}
      >
        {text}
      </text>
    </g>
  );
};
